import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAtelierStore } from "@store/atelierStore";
import { useDisponibiliteStore } from "@store/disponibiliteStore";
import { useDataStore } from "@store/user_org";
import { WorkCharge } from "../../types/garage/atelier";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchAlgolia from "@components/ui/search/SearchAlgolia";
import MiniCalendar from "@components/ui/MiniCalendar";
import { searchOrdresFull } from "@services/algolia";
import { ordresApi } from "@services/api/ordres";
import OrdreModal from "@components/modals/OrdreModal";
import type { Ordre } from "types/ordre";
import {
  ChargeSection,
  ChargeDropZone,
  StatusLegend,
} from "./ChargesSidebar";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { SplitModal } from "./SplitModal";
import { FloatingGhostOverlay } from "./FloatingGhostOverlay";
import { WorkerTimeGrid } from "./WorkerTimeGrid";
import { WeekTimeGridAtelier } from "./WeekTimeGridAtelier";
import { useAtelierDrag } from "./useAtelierDrag";

function tsToDateStr(ts?: { seconds: number }): string | null {
  if (!ts) return null;
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function AtelierScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchData = useAtelierStore((s) => s.fetchData);
  const fetchBlocks = useDisponibiliteStore((s) => s.fetchBlocks);
  const blocks = useDisponibiliteStore((s) => s.blocks);
  useEffect(() => {
    if (orgData?.id) {
      fetchData(orgData.id);
      fetchBlocks(orgData.id);
    }
  }, [orgData?.id]);

  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const setSelectedDate = useAtelierStore((s) => s.setSelectedDate);
  const viewMode = useAtelierStore((s) => s.viewMode);
  const setViewMode = useAtelierStore((s) => s.setViewMode);
  const activeSpecialty = useAtelierStore((s) => s.activeSpecialty);
  const setActiveSpecialty = useAtelierStore((s) => s.setActiveSpecialty);
  const [algoliaQuery, setAlgoliaQuery] = useState("");
  const charges = useAtelierStore((s) => s.charges);
  const workers = useAtelierStore((s) => s.workers);
  const selectedWeekWorkerId = useAtelierStore((s) => s.selectedWeekWorkerId);
  const setSelectedWeekWorkerId = useAtelierStore((s) => s.setSelectedWeekWorkerId);

  const chargesARepartir = charges.filter((c) => c.status === "a_repartir");
  const chargesReportees = charges.filter((c) => c.status === "reportee");
  const chargesEnPause = charges.filter((c) => c.status === "en_pause");

  const [splitTarget, setSplitTarget] = useState<WorkCharge | null>(null);
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);

  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const {
    jobDragRef,
    gridPreview,
    setGridPreview,
    floatingGhost,
    hoveredChargeSection,
    isDraggingJob,
    handleGlobalPointerMove,
    handleGlobalPointerUp,
    handleChargeDragStart,
    overDropZone,
  } = useAtelierDrag(gridScrollRef, sidebarRef);

  const handleSplitClick = useCallback((charge: WorkCharge) => {
    if (charge.hours > 0.5) setSplitTarget(charge);
  }, []);

  return (
    <div
      className="flex flex-col h-screen overflow-auto"
      style={{ background: "#FAFAFA" }}
      onPointerMove={handleGlobalPointerMove}
      onPointerUp={handleGlobalPointerUp}
    >
      <div className="flex flex-col h-fit">
        {/* Title */}
        <div className="flex flex-col px-4 lg:px-10 pt-8">
          <Text1>Planning atelier</Text1>
          <Text2>Répartissez les interventions ici.</Text2>
        </div>

        <div
          className="flex flex-row flex-1 px-4 lg:px-10 pt-5"
          style={{
            gap: 30,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left column */}
          <div
            ref={sidebarRef}
            className="flex flex-col"
            style={{
              width: 196,
              flexShrink: 0,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />
            <ChargeSection
              title="Charges à répartir"
              status="a_repartir"
              charges={chargesARepartir}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "a_repartir"}
            />
            <ChargeSection
              title="Charges reportées"
              status="reportee"
              charges={chargesReportees}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "reportee"}
            />
            <ChargeSection
              title="Charges en pause"
              status="en_pause"
              charges={chargesEnPause}
              onSplitClick={handleSplitClick}
              onChargeDragStart={handleChargeDragStart}
              isDropTarget={hoveredChargeSection === "en_pause"}
            />
            {isDraggingJob && <ChargeDropZone active={overDropZone} />}
            <StatusLegend />
          </div>

          {/* Right column */}
          <div
            className="flex flex-col flex-1"
            style={{
              gap: 10,
              minWidth: 0,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <div className="flex flex-row items-center" style={{ gap: 10 }}>
              <div style={{ width: 250, flexShrink: 0 }}>
                <SearchAlgolia
                  value={algoliaQuery}
                  setValue={setAlgoliaQuery}
                  placeholder="Rechercher un ordre..."
                  classNameCustom="w-full"
                  onSearch={async (query) => {
                    if (!orgData?.id) return [];
                    return searchOrdresFull(query, orgData.id);
                  }}
                  onAction={(option) => {
                    if (typeof option === "object" && option.value) {
                      ordresApi.get(option.value).then((ordre) => {
                        const date = tsToDateStr(ordre.reception_date) || tsToDateStr(ordre.created_at);
                        if (date) setSelectedDate(date);
                        setSelectedOrdre(ordre);
                      });
                      return true;
                    }
                  }}
                />
              </div>
              <ChunkyTabs
                activeTab={viewMode}
                tabs={[
                  { value: "jour", label: "Jour" },
                  { value: "semaine", label: "Semaine" },
                ]}
                setActiveTab={(tab) => setViewMode(tab as "jour" | "semaine")}
              />
              <ChunkyTabs
                activeTab={activeSpecialty}
                tabs={[
                  { value: "all", label: "Tous" },
                  { value: "autre", label: "Autre" },
                  { value: "carrosserie", label: "Carrosserie" },
                  { value: "mecanique", label: "Mécanique" },
                ]}
                setActiveTab={(tab) =>
                  setActiveSpecialty(
                    tab as "all" | "autre" | "carrosserie" | "mecanique",
                  )
                }
              />
            </div>
            {viewMode === "semaine" && (
              <div className="flex flex-row flex-wrap gap-1">
                {workers.map((w) => {
                  const isActive = (selectedWeekWorkerId || workers[0]?.id) === w.id;
                  return (
                    <button
                      key={w.id}
                      onClick={() => setSelectedWeekWorkerId(w.id)}
                      className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        isActive
                          ? "bg-white text-black shadow border border-neutral-200"
                          : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                      }`}
                      style={{ fontFamily: "Poppins" }}
                    >
                      {w.name}
                    </button>
                  );
                })}
              </div>
            )}
            {viewMode === "jour" ? (
              <WorkerTimeGrid
                gridScrollRef={gridScrollRef}
                gridPreview={gridPreview}
                setGridPreview={setGridPreview}
                jobDragRef={jobDragRef}
                blocks={blocks}
              />
            ) : (
              <WeekTimeGridAtelier
                blocks={blocks}
                workerId={selectedWeekWorkerId || workers[0]?.id}
                gridScrollRef={gridScrollRef}
                gridPreview={gridPreview}
                setGridPreview={setGridPreview}
                jobDragRef={jobDragRef}
              />
            )}
          </div>
        </div>

        {floatingGhost && <FloatingGhostOverlay ghost={floatingGhost} />}
        {splitTarget && (
          <SplitModal
            charge={splitTarget}
            onClose={() => setSplitTarget(null)}
          />
        )}
        {selectedOrdre && (
          <OrdreModal
            ordre={selectedOrdre}
            onClose={() => setSelectedOrdre(null)}
            onSave={() => setSelectedOrdre(null)}
          />
        )}
      </div>
    </div>
  );
}
